import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, X, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BUSINESS, telLink, NAV_LINKS } from "@/data/business";
import { btnPrimary } from "@/lib/ui";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-shadow duration-300 ${
        scrolled ? "shadow-[0_4px_24px_rgba(15,61,94,0.08)]" : ""
      } bg-white/85 backdrop-blur-xl border-b border-gray-100`}
      data-testid="site-header"
    >
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? "text-brandgreen" : "text-gray-700 hover:text-navy"
                  }`
                }
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={telLink}
              className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-brandgreen transition-colors"
              data-testid="header-call"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
            </a>
            <Link to="/quote" className={`${btnPrimary} !px-6 !py-2.5 text-sm`} data-testid="header-quote">
              Get Quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <button
            className="lg:hidden grid place-items-center w-11 h-11 rounded-xl text-navy hover:bg-neutralbg transition-colors"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-white shadow-2xl p-6 flex flex-col fade-up">
            <div className="flex items-center justify-between">
              <Logo />
              <button
                className="grid place-items-center w-11 h-11 rounded-xl text-navy hover:bg-neutralbg"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="mt-8 flex flex-col gap-1" aria-label="Mobile">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-3.5 rounded-xl text-base font-medium transition-colors ${
                      isActive ? "bg-brandgreen/10 text-brandgreen-dark" : "text-gray-800 hover:bg-neutralbg"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-auto pt-6 flex flex-col gap-3">
              <a href={telLink} className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy px-6 py-3 font-semibold text-navy">
                <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
              </a>
              <Link to="/quote" className={btnPrimary}>
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
